import { GALAXY_BACKGROUND_COLOR } from './graphConstants'
import type {
  ExtrapolatedGraphAnnotation,
  ExtrapolatedGraphBottomAxisLabel,
  FormattedGraphValue,
  GalaxyScaleSettings,
  GraphPadding,
  GraphPoint,
} from './types'

export const addGalaxyTextShadow = (
  annotations: ExtrapolatedGraphAnnotation[],
  showGalaxy: boolean
) => annotations.map(annotation => showGalaxy
  ? { ...annotation, textShadowColor: GALAXY_BACKGROUND_COLOR }
  : annotation
)

export const createFutureValueAnnotation = ({
  showTimelineUnits,
  lastFutureProjectedPoint,
  xForTimestamp,
  yForValue,
  formatProjectedValue,
}: {
  showTimelineUnits: boolean
  lastFutureProjectedPoint?: GraphPoint
  xForTimestamp: (timestamp: number) => number
  yForValue: (value: number) => number
  formatProjectedValue: (value: number) => string
}): ExtrapolatedGraphAnnotation | undefined =>
  showTimelineUnits && lastFutureProjectedPoint
    ? {
      key: 'future-value',
      x: xForTimestamp(lastFutureProjectedPoint.timestamp) - 8,
      y: yForValue(lastFutureProjectedPoint.value) + 6,
      text: formatProjectedValue(lastFutureProjectedPoint.value),
      transform: 'translate(-100%, -100%)',
      pointerEvents: 'none' as const,
      opacity: 0.5,
    }
    : undefined

export const createSideUnitAnnotations = ({
  showSideUnits,
  yDomainMax,
  yForValue,
}: {
  showSideUnits: boolean
  yDomainMax: number
  yForValue: (value: number) => number
}): ExtrapolatedGraphAnnotation[] =>
  showSideUnits
    ? [1, 2].map(sideUnitStep => {
      const sideUnitValue = (yDomainMax * sideUnitStep) / 3
      return {
        key: `side-unit-${sideUnitStep}`,
        x: 0,
        y: yForValue(sideUnitValue),
        text: `${Math.round(sideUnitValue)}x`,
        transform: 'translateY(-50%)',
        pointerEvents: 'none' as const,
        opacity: 0.55,
      }
    })
    : []

export const createCurrentValueLabelPosition = ({
  currentValueLabelPoint,
  xForTimestamp,
  yForValue,
}: {
  currentValueLabelPoint?: GraphPoint
  xForTimestamp: (timestamp: number) => number
  yForValue: (value: number) => number
}) =>
  currentValueLabelPoint
    ? {
      x: xForTimestamp(currentValueLabelPoint.timestamp),
      y: yForValue(currentValueLabelPoint.value),
    }
    : undefined

export const createCurrentValueLabel = ({
  showTimelineUnits,
  currentValue,
  formatCurrentValue,
}: {
  showTimelineUnits: boolean
  currentValue?: number
  formatCurrentValue: (value: number) => FormattedGraphValue
}) =>
  showTimelineUnits && currentValue !== undefined
    ? formatCurrentValue(currentValue)
    : undefined

// Half the rendered width of a 4-digit year tick at fontSize 12 (IBM Plex
// Mono, ~28px total). The middle-anchored current-year label's center is
// clamped at least this far right of the plot's left edge, so while the
// timeline cursor is pinned at the start the label reads LEFT-ALIGNED with the
// plot edge instead of overflowing half its width to the left.
const CURRENT_YEAR_LABEL_HALF_WIDTH_PX = 14

export const createBottomAxisLabels = ({
  showGalaxy,
  showAllYears,
  currentYear,
  leftYear,
  rightYear,
  currentTimelineX,
  padding,
  innerWidth,
  width,
  galaxySettings,
  xForTimestamp,
}: {
  showGalaxy: boolean
  showAllYears: boolean
  currentYear: number
  leftYear: number
  rightYear: number
  currentTimelineX: number
  padding: GraphPadding
  innerWidth: number
  width: number
  galaxySettings: GalaxyScaleSettings
  xForTimestamp: (timestamp: number) => number
}): ExtrapolatedGraphBottomAxisLabel[] => {
  if (showGalaxy) {
    return galaxySettings.tickYears.map(year => ({
      key: `${year}`,
      x: xForTimestamp(new Date(year, 0, 1).getTime()),
      text: `${year}`,
      textAnchor: year === galaxySettings.maxYear ? 'end' as const : 'middle' as const,
      opacity: 0.7,
    }))
  }
  if (showAllYears) {
    const allYears = Array.from(
      { length: Math.max(1, rightYear - leftYear + 1) },
      (_, index) => leftYear + index
    )
    const visibleYears = allYears.length > 8
      ? allYears.filter(year => Math.abs(year - currentYear) % 2 === 0 || year === currentYear)
      : allYears
    return visibleYears.map((year, index) => ({
      key: `${year}`,
      x: visibleYears.length === 1
        ? padding.left + (innerWidth / 2)
        : padding.left + ((innerWidth * index) / (visibleYears.length - 1)),
      text: `${year}`,
      textAnchor: index === 0
        ? 'start' as const
        : index === visibleYears.length - 1
          ? 'end' as const
          : 'middle' as const,
      opacity: year === currentYear ? 0.9 : 0.8,
      fill: year === currentYear ? undefined : '#aaa',
    }))
  }
  // NB: this default (non-pill, non-explicit-years) path renders only on the
  // mobile top graph — the desktop HUD uses currentYearPill (empty bottom axis)
  // and the takeover-risk figures pass explicit bottomAxisYears. So the tweaks
  // below are scoped to that graph in practice: on the narrow mobile width we
  // keep just two solid-black labels — the current year and the end year —
  // dropping the left-edge year and the interior 2030 tick, which crowded the
  // axis and overlapped the current-year label.
  return [
    {
      key: `${currentYear}`,
      x: Math.max(
        currentTimelineX,
        padding.left + CURRENT_YEAR_LABEL_HALF_WIDTH_PX,
      ),
      text: `${currentYear}`,
      textAnchor: 'middle' as const,
      opacity: 0.9,
    },
    ...(rightYear !== currentYear && rightYear !== leftYear
      ? [{
        key: `${rightYear}`,
        x: width - padding.right,
        text: `${rightYear}`,
        textAnchor: 'end' as const,
        opacity: 0.9,
        dasharray: '5,5',
      }]
      : []),
  ]
}
